import Delegate from 'ftdomdelegate/main';
import { waitForEvent } from '../../utils';

const DialogElement = class extends HTMLElement {
    static get observedAttributes() {
        return ['id', 'open'];
    }

    constructor() {
        super();

        this.addEventListener('dialog:force-close', (event) => {
            this.hide();
            event.stopPropagation();
        });
    }

    connectedCallback() {
        if (this.id) {
            this.delegate.off().on('click', `[aria-controls="${this.id}"]`, this.onToggleClicked.bind(this));
        }

        this.abortController = new AbortController();
        this.setAttribute('role', 'dialog');
    }

    disconnectedCallback() {
        this.abortController.abort();
        this.delegate.off();
    }

    show() {
        if (this.open) return false;
        this.setAttribute('open', '');
        return waitForEvent(this, 'dialog:after-show');
    }

    hide() {
        if (!this.open) return false;
        this.removeAttribute('open');
        return waitForEvent(this, 'dialog:after-hide');
    }

    get delegate() {
        this.delegateInstance = this.delegateInstance || new Delegate(document.body);
        return this.delegateInstance;
    }

    get controls() {
        return Array.from(this.getRootNode().querySelectorAll(`[aria-controls="${this.id}"]`));
    }

    get open() {
        return this.hasAttribute('open');
    }

    get shouldAppendToBody() { // eslint-disable-line class-methods-use-this
        return false;
    }

    get shouldLock() { // eslint-disable-line class-methods-use-this
        return false;
    }

    attributeChangedCallback(name, oldValue, newValue) {
        switch (name) {
            case 'id':
                if (newValue) {
                    this.delegate.off().on('click', `[aria-controls="${this.id}"]`, this.onToggleClicked.bind(this));
                }
                break;
            case 'open':
                this.controls.forEach((toggle) => toggle.setAttribute('aria-expanded', newValue === null ? 'false' : 'true'));

                if (oldValue === null && (newValue === '' || newValue === 'immediate')) {
                    this.removeAttribute('inert');
                    this.originalParentBeforeAppend = null;

                    if (this.shouldAppendToBody && this.parentElement !== document.body) {
                        this.originalParentBeforeAppend = this.parentElement;
                        document.body.append(this);
                    }

                    const showTransitionPromise = this.showTransition(newValue !== 'immediate') || Promise.resolve();

                    showTransitionPromise.then(() => {
                        this.dispatchEvent(new CustomEvent('dialog:after-show', { bubbles: true }));
                    });
                } else if (oldValue !== null && newValue === null) {
                    this.setAttribute('inert', '');

                    const hideTransitionPromise = this.hideTransition() || Promise.resolve();

                    hideTransitionPromise.then(() => {
                        if (this.parentElement === document.body
                            && this.originalParentBeforeAppend) {
                            this.originalParentBeforeAppend.appendChild(this);
                            this.originalParentBeforeAppend = null;
                        }
                        this.dispatchEvent(new CustomEvent('dialog:after-hide', { bubbles: true }));
                    });
                }

                this.dispatchEvent(new CustomEvent('toggle', { bubbles: true }));
                break;

            // no default
        }
    }

    showTransition() {} // eslint-disable-line no-unused-vars, class-methods-use-this

    hideTransition() {} // eslint-disable-line class-methods-use-this

    onToggleClicked(event) {
        event?.preventDefault();

        if (this.open) {
            this.hide();
            return;
        }

        this.show(event);
    }
};

export default DialogElement;
